import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DraggableModalPanel } from '../ui/DraggableModalPanel';
import { RoundedCheckbox } from '../ui/RoundedCheckbox';

export type CatalogSelectItem = {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  /** Campo auxiliar (ex.: URL de recurso, cargo de persona). */
  extra?: string;
};

export type CatalogSelectPage = {
  items: CatalogSelectItem[];
  totalCount: number;
  page: number;
  pageSize: number;
};

type CatalogMultiSelectModalProps = {
  open: boolean;
  title: string;
  subtitle?: string;
  /** Itens já vinculados ao produto (aparecem marcados e desabilitados). */
  alreadySelectedIds: string[];
  pageSize?: number;
  searchPlaceholder?: string;
  fetchPage: (page: number, pageSize: number, search: string) => Promise<CatalogSelectPage>;
  onClose: () => void;
  onConfirm: (selected: CatalogSelectItem[]) => void;
};

export function CatalogMultiSelectModal({
  open,
  title,
  subtitle,
  alreadySelectedIds,
  pageSize = 25,
  searchPlaceholder = 'Buscar por nome ou código...',
  fetchPage,
  onClose,
  onConfirm,
}: CatalogMultiSelectModalProps) {
  const [items, setItems] = useState<CatalogSelectItem[]>([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [initialLoading, setInitialLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [selectedItems, setSelectedItems] = useState<Map<string, CatalogSelectItem>>(() => new Map());
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const alreadySelectedRef = useRef(new Set(alreadySelectedIds));
  const fetchPageRef = useRef(fetchPage);
  const requestIdRef = useRef(0);
  const hasItemsRef = useRef(false);
  const activeSearchRef = useRef('');

  fetchPageRef.current = fetchPage;
  hasItemsRef.current = items.length > 0;
  const hasMore = items.length < totalCount;
  const isBusy = initialLoading || refreshing || loadingMore;

  const loadPage = useCallback(
    async (nextPage: number, append: boolean, search: string): Promise<void> => {
      const requestId = ++requestIdRef.current;

      if (append) {
        setLoadingMore(true);
      } else if (hasItemsRef.current) {
        setRefreshing(true);
      } else {
        setInitialLoading(true);
      }

      try {
        const result = await fetchPageRef.current(nextPage, pageSize, search);
        if (requestId !== requestIdRef.current || activeSearchRef.current !== search) {
          return;
        }

        setTotalCount(result.totalCount);
        setPage(result.page);
        setItems((current) => {
          if (!append) {
            return result.items;
          }

          const existing = new Set(current.map((item) => item.id));
          return [...current, ...result.items.filter((item) => !existing.has(item.id))];
        });
      } catch {
        if (requestId === requestIdRef.current) {
          toast.error('Falha ao carregar catálogo.');
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setInitialLoading(false);
          setRefreshing(false);
          setLoadingMore(false);
        }
      }
    },
    [pageSize],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    alreadySelectedRef.current = new Set(alreadySelectedIds);
    setSelectedIds(new Set());
    setSelectedItems(new Map());
    setSearchInput('');
    setDebouncedSearch('');
    setItems([]);
    setPage(0);
    setTotalCount(0);
    hasItemsRef.current = false;
    // Reinicia só ao abrir; alreadySelectedIds é lido no momento da abertura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 400);

    return () => window.clearTimeout(handle);
  }, [open, searchInput]);

  useEffect(() => {
    if (!open) {
      return;
    }

    activeSearchRef.current = debouncedSearch;
    void loadPage(1, false, debouncedSearch);
  }, [open, debouncedSearch, loadPage]);

  useEffect(() => {
    if (!open || !hasMore || isBusy) {
      return;
    }

    const sentinel = sentinelRef.current;
    const root = listRef.current;
    if (!sentinel || !root) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadPage(page + 1, true, activeSearchRef.current);
        }
      },
      { root, rootMargin: '120px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMore, isBusy, page, loadPage]);

  function toggleItem(item: CatalogSelectItem): void {
    if (alreadySelectedRef.current.has(item.id)) {
      return;
    }

    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
      }
      return next;
    });

    setSelectedItems((current) => {
      const next = new Map(current);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.set(item.id, item);
      }
      return next;
    });
  }

  function handleConfirm(): void {
    onConfirm(Array.from(selectedItems.values()));
    onClose();
  }

  return (
    <DraggableModalPanel
      open={open}
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      className="w-full max-w-2xl"
      footer={
        <div className="erp-btn-actions">
          <button type="button" className="erp-btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="erp-btn-primary"
            disabled={selectedIds.size === 0}
            onClick={handleConfirm}
          >
            Confirmar seleção ({selectedIds.size})
          </button>
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <label className="erp-label">
          Buscar
          <input
            className="erp-input"
            value={searchInput}
            placeholder={searchPlaceholder}
            autoComplete="off"
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>

        <p className="text-sm text-secondary-600">
          {selectedIds.size} selecionado(s) nesta sessão
          {totalCount > 0 ? ` · ${totalCount} no catálogo` : null}
          {debouncedSearch ? ` · filtro “${debouncedSearch}”` : null}
          {refreshing ? ' · atualizando...' : null}
        </p>

        <div
          ref={listRef}
          className={`min-h-[16rem] max-h-[50vh] flex-1 overflow-y-auto rounded-lg border border-secondary-200 transition-opacity ${
            refreshing ? 'opacity-70' : 'opacity-100'
          }`}
        >
          {initialLoading && items.length === 0 ? (
            <p className="p-4 text-sm text-secondary-500">Carregando...</p>
          ) : items.length === 0 ? (
            <p className="p-4 text-sm text-secondary-500">
              {debouncedSearch ? 'Nenhum resultado para a busca.' : 'Nenhum item disponível.'}
            </p>
          ) : (
            <ul className="divide-y divide-secondary-100">
              {items.map((item) => {
                const linked = alreadySelectedRef.current.has(item.id);
                const checked = linked || selectedIds.has(item.id);

                return (
                  <li key={item.id}>
                    <label
                      className={`flex items-start gap-3 px-4 py-3 ${
                        linked ? 'cursor-default bg-secondary-50/80' : 'cursor-pointer hover:bg-secondary-50'
                      }`}
                    >
                      <RoundedCheckbox
                        className="mt-0.5"
                        checked={checked}
                        disabled={linked}
                        onChange={() => toggleItem(item)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="font-medium text-secondary-900">{item.title}</span>
                        {item.subtitle ? (
                          <span className="ml-2 font-mono text-xs text-secondary-500">
                            {item.subtitle}
                          </span>
                        ) : null}
                        {linked ? (
                          <span className="ml-2 text-xs font-medium text-primary-700">Já vinculado</span>
                        ) : null}
                        {item.description ? (
                          <p className="mt-1 line-clamp-2 text-sm text-secondary-600">
                            {item.description}
                          </p>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          <div ref={sentinelRef} className="h-2" />
          {loadingMore ? (
            <p className="px-4 py-2 text-center text-xs text-secondary-500">Carregando mais...</p>
          ) : null}
        </div>
      </div>
    </DraggableModalPanel>
  );
}
